import React, { useState, useEffect } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { addToCart, setCartCount } from "../../redux/cartSlice";
import productDetails from "../../data/productDetails.json";
import subcategoryVariants from "../../data/subcategoryVariants.json";
import { useCustomToast } from "../../context/CustomToastContext";
import { useTranslation } from "../../context/TranslationContext";
import HomeApi from "../../apiProvider/homeApi";
import apiCart from "../../apiProvider/addToCartApi";
import { BASE_URL, IMAGE_URL } from "../../network/apiClient";
import { getOrGenerateGuestId } from "../../utils/guestIdHelper";
import Select from "react-select";
import apiContactUs from "../../apiProvider/contactusApi";


// Detect if product has O-ring standard data or is in O-ring category
const isOringProduct = (product) =>
    (product?.subCategoryDetails?.[0]?.origins === true);

const isOthersProduct = (product) =>
    (product?.subCategoryDetails?.[0]?.others === true);

const isVariantProduct = (product) =>
    (product?.subCategoryDetails?.[0]?.variants === true);


// Build O-ring spec rows from product
const buildOringSpecs = (product) => {
    const rows = [];
    const customerAttribute = product?.customerAttribute || [];
    const COMMON_KEYS = [
        "standard", "sizeCode", "metric_id_mm", "metric_id_tolerance_mm",
        "metric_cs_mm", "metric_cs_tolerance_mm", "baseSku", "sku", "materials", "_id", "currencyDetails"
    ];

    let dynamicKeys = new Set();
    customerAttribute.forEach((attr, attrIdx) => {
        const materials = attr.materials || [];
        materials.forEach((m, mIdx) => {
            const row = {
                _id: m._id,
                productId: product?._id,
                uniqueId: `oring-${product?._id}-${attrIdx}-${mIdx}`,
                standard: attr.standard,
                productName: product?.productName,
                partNo: attr.sizeCode,
                id: attr.metric_id_mm,
                idTolerance: attr.metric_id_tolerance_mm,
                cs: attr.metric_cs_mm,
                csTolerance: attr.metric_cs_tolerance_mm,
                // sku: m.sku,
                stock: m.stock,
                pricing: m.pricing,
                currencyDetails: attr.currencyDetails,
            };

            // Add extra fields from attr
            Object.keys(attr).forEach(key => {
                if (!COMMON_KEYS.includes(key) && key !== "_id") {
                    row[key] = attr[key];
                    dynamicKeys.add(key);
                }
            });
            rows.push(row);
        });
    });

    const columns = Array.from(dynamicKeys).map(key => ({
        key,
        label: key.charAt(0).toUpperCase() + key.slice(1)
    }));

    return { columns, rows };
};


// Build dynamic variant rows from customerAttribute.rowData
// Returns: { columns: [{key, label}], rows: [...] }
const buildVariantData = (product) => {
    const rowData = product?.customerAttribute || [];
    const COMMON_KEYS = ["price", "stock", "sku", "baseSku", "currencyDetails", "pricing"];

    let extraKeys = [];
    if (rowData.length > 0) {
        const firstRow = rowData[0];
        extraKeys = Object.keys(firstRow).filter((k) => !COMMON_KEYS.includes(k) && k !== "_id" && k !== "currencyDetails");
    }

    const columns = [
        { key: "productName", label: "Product" },
        ...extraKeys.map((k) => ({ key: k, label: k.charAt(0).toUpperCase() + k.slice(1) })),
        // { key: "sku", label: "SKU" },
        // { key: "price", label: "Price" },
    ];

    const rows = rowData.map((row, i) => ({
        ...row,
        productId: product?._id,
        productName: product?.productName,
        uniqueId: `variant-${product?._id}-${i}`,
        currencyDetails: row.currencyDetails || null
    }));
    return { columns, rows };
};




const ProductSpecs = () => {
    const { subCategoryId, categoryId } = useParams();
    const [loading, setLoading] = useState(true);
    const [specs, setSpecs] = useState([]);
    const [variantData, setVariantData] = useState(null); // Store full variant data
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const initialSearch = queryParams.get("search") || "";

    const [searchTerm, setSearchTerm] = useState(initialSearch);
    const [currentPage, setCurrentPage] = useState(1);
    const [quantities, setQuantities] = useState({});
    const [productList, setProductList] = useState([])
    const [selectedProductId, setSelectedProductId] = useState(location.state?.productId || "");
    const [selectedMaterialId, setSelectedMaterialId] = useState("");
    const [materialOptionsList, setMaterialOptionsList] = useState([]);
    const [heroProduct, setHeroProduct] = useState(null);

    const dispatch = useDispatch();
    const selectedCurrency = useSelector((state) => state.currency.selectedCurrency);
    const { translateSync } = useTranslation();
    const { showToast } = useCustomToast();

    const [oringSpecs, setOringSpecs] = useState([]);   // O-ring rows
    const [oringCols, setOringCols] = useState([]);     // dynamic O-ring columns
    const [variantCols, setVariantCols] = useState([]);   // dynamic columns
    const [variantRows, setVariantRows] = useState([]);   // dynamic rows
    const [productType, setProductType] = useState("oring");

    const [attributeConfigs, setAttributeConfigs] = useState([]); // Objects like {name: 'colours', ...}
    const [attributeOptions, setAttributeOptions] = useState({}); // Map of name -> [values]
    const [selectedFilters, setSelectedFilters] = useState({}); // Current user selections
    const [dropdownProducts, setDropdownProducts] = useState([]); // Full list for the dropdown

    const [inquiryForm, setInquiryForm] = useState({
        username: "",
        phone: "",
        address: "",
        message: ""
    });
    const [inquiryErrors, setInquiryErrors] = useState({});

    const customStyles = {
        control: (provided, state) => ({
            ...provided,
            borderColor: ' #ddd',
            boxShadow: null,
            '&:hover': {
                borderColor: '#ddd'
            },
            minHeight: '50px',
            height: '50px',
            borderRadius: '8px',

        }),
        valueContainer: (provided) => ({
            ...provided,
            height: '50px',
            padding: '0 6px'
        }),
        input: (provided) => ({
            ...provided,
            margin: '0px',
        }),
        indicatorsContainer: (provided) => ({
            ...provided,
            height: '50px',
        }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected
                ? 'var(--primary-color)'
                : state.isFocused
                    ? '#EEEAE5'
                    : null,
            color: state.isSelected ? 'white' : 'var(--primary-color)',
            '&:active': {
                backgroundColor: 'var(--primary-color)',
                color: 'white'
            }
        }),
        singleValue: (provided) => ({
            ...provided,
            color: 'var(--primary-color)'
        }),
        menuPortal: (provided) => ({ ...provided, zIndex: 9999 }),
        menu: (provided) => ({ ...provided, zIndex: 9999 })
    };


    const ITEMS_PER_PAGE = 10;
    const fetchProducts = async (currentSearch = searchTerm) => {
        try {
            setLoading(true);
            const params = {
                limit: 1000,
                categoryId: categoryId === "all" ? undefined : categoryId,
                subCategoryId: subCategoryId === "all" ? undefined : subCategoryId,
                search: currentSearch || "",
                productId: productType === "oring" ? undefined : (selectedProductId || undefined),
                materialId: productType === "oring" ? (selectedMaterialId || undefined) : undefined,
                currency: selectedCurrency?.value
            };

            // Flatten dynamic filters: filters[colours]=black
            Object.entries(selectedFilters).forEach(([key, value]) => {
                if (value && value !== "") {
                    params[`filters[${key}]`] = value;
                }
            });
            const response = await HomeApi.ProductList(params);
            console.log("fetchProducts_HomeApi_response: ", response);

            if (response.status) {
                const products = response.response?.data || [];
                setProductList(products);

                if (products.length > 0) {
                    if (selectedProductId) {
                        // If a specific product was requested, use it as hero
                        handleSelectProduct(selectedProductId, products);
                    } else {
                        // Show all variants from all products by default
                        handleSelectProduct("", products);
                    }
                } else {
                    handleSelectProduct(selectedProductId || null, []);
                }
            }
        } catch (error) {
            console.error("Error fetching Products :", error);
        } finally {
            setLoading(false);
        }
    }

    // Fetch only the product list for the dropdown
    const fetchProductList = async () => {
        try {
            const params = {
                limit: 1000,
                categoryId: categoryId === "all" ? undefined : categoryId,
                subCategoryId: subCategoryId === "all" ? undefined : subCategoryId,
            };
            const response = await HomeApi.ProductList(params);
            if (response.status) {
                const products = response.response?.data || [];
                setDropdownProducts(products);
            }
        } catch (error) {
            console.error("Error fetching product list for dropdown:", error);
        }
    };

    const fetchMaterials = async () => {
        try {
            const response = await HomeApi.getMaterialList();
            if (response.status) {
                // Robustly handle both paginated { data: [...] } and direct array [...] responses
                const materialData = response.response?.data || (Array.isArray(response.response) ? response.response : []);
                setMaterialOptionsList(materialData);
            }
        } catch (error) {
            console.error("Error fetching materials:", error);
        }
    };

    const fetchFilterMetadata = async () => {
        try {
            const params = {
                limit: 100,
                isFilterCheckbox: true
            };
            const response = await apiCart.variantList(params);
            if (response.status && response.response?.data) {
                const configs = response.response.data;
                setAttributeConfigs(configs);
                // Fetch options for each attribute name
                configs.forEach(conf => {
                    if (conf.name) fetchOptionsForAttribute(conf.name);
                });
            }
        } catch (error) {
            console.error("Error fetching filter metadata:", error);
        }
    };

    const fetchOptionsForAttribute = async (attrName) => {
        try {
            const result = await apiCart.getAttributeValues(attrName);
            if (result.status && result.response?.data) {
                setAttributeOptions(prev => ({
                    ...prev,
                    [attrName]: result.response.data
                }));
            }
        } catch (error) {
            console.error(`Error fetching options for ${attrName}:`, error);
        }
    };

    const handleSelectProduct = (productId, list = productList) => {
        // This now synchronizes UI state based on the provided list (usually from API)
        if (!list || list.length === 0 || productId === null) {
            setHeroProduct(null);
            setProductType("none");
            setOringSpecs([]);
            setVariantCols([]);
            setVariantRows([]);
            return;
        }

        if (productId === "" || (productId === "all" && list.length > 1)) {
            // Logic for combining results (Show All)
            const oringProds = list.filter(p => isOringProduct(p));
            const othersProds = list.filter(p => isOthersProduct(p));
            const variantProds = list.filter(p => isVariantProduct(p));

            if (oringProds.length > 0) {
                console.error("oringProds: ", oringProds);
                setHeroProduct(oringProds[0]);
                setProductType("oring");

                const allSpecs = [];
                let allCols = new Set();
                oringProds.forEach(p => {
                    const { columns, rows } = buildOringSpecs(p);
                    console.error("columns: ", columns);
                    console.error("rows: ", rows);
                    allSpecs.push(...rows);
                    columns.forEach(c => allCols.add(JSON.stringify(c)));
                });

                setOringSpecs(allSpecs);
                setOringCols(Array.from(allCols).map(c => JSON.parse(c)));
                console.error("setOringCols: ", allCols);
                setVariantCols([]);
                setVariantRows([]);
            } else if (othersProds.length > 0) {
                setHeroProduct(othersProds[0]);
                setProductType("others");
                setOringSpecs([]);
                setVariantCols([]);
                setVariantRows([]);
            } else if (variantProds.length > 0) {
                setHeroProduct(variantProds[0]);
                setProductType("variant");

                const allRows = [];
                let allCols = new Set();
                variantProds.forEach((p, pIdx) => {
                    const { columns, rows } = buildVariantData(p);
                    const prefixedRows = rows.map(r => ({ ...r, uniqueId: `p${pIdx}-${r.uniqueId}` }));
                    allRows.push(...prefixedRows);
                    columns.forEach(c => allCols.add(JSON.stringify(c)));
                });

                setVariantRows(allRows);
                setVariantCols(Array.from(allCols).map(c => JSON.parse(c)));
                setOringSpecs([]);
                setOringCols([]);
            }
            return;
        }

        // Single product display (UI sync)
        const hero = list.find(p => p._id === productId) || list[0];
        if (hero) {
            setHeroProduct(hero);
            if (isOthersProduct(hero)) {
                setProductType("others");
                setOringSpecs([]);
                setVariantCols([]);
                setVariantRows([]);
            } else if (isOringProduct(hero)) {
                setProductType("oring");
                const { columns, rows } = buildOringSpecs(hero);
                setOringSpecs(rows);
                setOringCols(columns);
                setVariantCols([]);
                setVariantRows([]);
            } else if (isVariantProduct(hero)) {
                setProductType("variant");
                const { columns, rows } = buildVariantData(hero);
                setVariantCols(columns);
                setVariantRows(rows);
                setOringSpecs([]);
                setOringCols([]);
            } else {
                setProductType("none");
                setOringSpecs([]);
                setVariantCols([]);
                setVariantRows([]);
            }
        }
    };

    useEffect(() => {
        window.scrollTo(0, 0);
        setSelectedMaterialId("");
        setSelectedProductId("");
        setCurrentPage(1);
        fetchProductList();
        fetchProducts();
        fetchFilterMetadata();
        fetchMaterials();
    }, [subCategoryId, categoryId]);

    // Handle filter or product changes
    useEffect(() => {
        if (!loading) {
            fetchProducts(searchTerm);
        }
    }, [selectedFilters, selectedProductId, selectedMaterialId, selectedCurrency]);

    // Handle initial search from URL or State
    useEffect(() => {
        const queryTerm = new URLSearchParams(location.search).get("search");
        if (queryTerm !== null) {
            setSearchTerm(queryTerm);
        }

        // Handle productId from location state (navigation from ProductsMain)
        if (location.state?.productId) {
            setSelectedProductId(location.state.productId);
        }
    }, [location.search, location.state]);

    // Debounced search
    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            if (searchTerm !== initialSearch || searchTerm === "") {
                fetchProducts(searchTerm);
            }
        }, 500);

        return () => clearTimeout(delayDebounceFn);
    }, [searchTerm]);

    const activeRows = productType === "oring" ? oringSpecs : variantRows;
    const filteredRows = activeRows;

    const totalPages = Math.ceil(filteredRows.length / ITEMS_PER_PAGE);
    const indexOfFirst = (currentPage - 1) * ITEMS_PER_PAGE;
    const indexOfLast = indexOfFirst + ITEMS_PER_PAGE;
    const currentRows = filteredRows.slice(indexOfFirst, indexOfLast);
    console.error("currentRows", currentRows);


    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    const handleQuantityChange = (uniqueId, value, maxStock) => {
        let val = parseInt(value);
        if (isNaN(val) || val < 1) val = 1;
        if (maxStock !== undefined && val > Number(maxStock)) val = Number(maxStock);
        setQuantities({
            ...quantities,
            [uniqueId]: val
        });
    };

    const handleAddToCart = async (item) => {
        console.error("item", item);
        const qty = quantities[item.uniqueId] || 1;

        let price = Number(item.price || 0);
        if (item.pricing && selectedCurrency) {
            const priceObj = item.pricing.find(p => p.currencyId === selectedCurrency.value);
            if (priceObj) price = priceObj.price;
        }
        const totalPrice = price * qty;

        // Calculate variant name (use last extra attribute value for generic variants)
        let name = item.partNo || /* item.sku || */ "item";
        if (productType === "variant") {
            const attrKeys = variantCols.filter(c => !["price", "stock", "sku", "customermrp", "mrp", "weight", "_id"].includes(c.key));
            if (attrKeys.length > 0) {
                name = item[attrKeys[attrKeys.length - 1].key] || name;
            }
        }

        // Guest/User ID logic
        const token = localStorage.getItem('userToken');
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const guestId = getOrGenerateGuestId();

        const variantInfo = {
            variantName: productType === "oring" ? item.partNo : name,
            // sku: item.sku,
        };

        if (productType === "oring") {
            if (item.id) variantInfo.id = item.id;
            if (item.idTolerance) variantInfo.idTolerance = item.idTolerance;
            if (item.cs) variantInfo.cs = item.cs;
            if (item.csTolerance) variantInfo.csTolerance = item.csTolerance;
        } else {
            // Add other variant attributes
            variantCols
                .filter((c) => !["price", "stock", "sku", "customermrp", "mrp", "weight", "_id", "pricing", "currencyDetails"].includes(c.key))
                .forEach((c) => {
                    if (item[c.key]) variantInfo[c.key] = item[c.key];
                });
        }

        variantInfo.attributes = {
            pricing: item.pricing || []
        };

        let payload = {
            products: [{ productId: item.productId || heroProduct?._id, variantId: item._id, quantity: qty, mrpPrice: price, ...variantInfo }],
            total: totalPrice,
            subtotal: totalPrice
        };

        if (selectedCurrency) {
            payload.currency = selectedCurrency.value;
        }

        if (token && user._id) {
            payload.type = 'user';
            payload.userId = user._id;
            payload.userType = 'user';
        } else {
            payload.type = 'guest';
            payload.guestUserId = guestId;
            payload.userType = 'guest';
        }

        try {
            const result = await apiCart.addToCart(payload);
            if (result && result.status) {
                showToast(`Product Added to Cart Successfully`, "success");
                // 2. Fetch Helper: Logic to get accurate count from server
                const fetchUpdatedCount = async () => {
                    let countResult;
                    if (token && user._id) {
                        countResult = await apiCart.getCartCount(user._id, { type: 'user', userType: 'user' });
                    } else {
                        countResult = await apiCart.getCartCount(guestId, { type: 'guest', userType: 'guest' });
                    }

                    if (countResult?.status && countResult?.response?.data) {
                        dispatch(setCartCount(countResult.response.data.count || 0));
                    }
                };

                await fetchUpdatedCount();


            } else {
                showToast(result?.response || 'Failed to add to cart', "error");
            }
        } catch (error) {
            console.error("Add to cart error", error);
            showToast('An error occurred while adding to cart.', "error");
        }
    };

    const handleInquiryChange = (e) => {
        const { name, value } = e.target;

        if (name === "phone") {
            // Only allow numbers
            const onlyNums = value.replace(/[^0-9]/g, '');
            setInquiryForm(prev => ({ ...prev, [name]: onlyNums }));
        } else {
            setInquiryForm(prev => ({ ...prev, [name]: value }));
        }

        // Clear error when user starts typing
        if (inquiryErrors[name]) {
            setInquiryErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    };

    const handleInquirySubmit = async (e) => {
        e.preventDefault();

        const newErrors = {};
        if (!inquiryForm.username) newErrors.username = "Name is required";
        if (!inquiryForm.phone) newErrors.phone = "Phone number is required";
        if (!inquiryForm.address) newErrors.address = "Address is required";
        if (!inquiryForm.message) newErrors.message = "Message is required";


        if (Object.keys(newErrors).length > 0) {
            setInquiryErrors(newErrors);
            return;
        }

        try {
            const payload = {
                productId: heroProduct?._id,
                name: inquiryForm.username,
                phone: inquiryForm.phone,
                address: inquiryForm.address,
                message: inquiryForm.message
            };

            const result = await HomeApi.productEnquiry(payload);

            if (result.status) {
                // Clear form on success
                setInquiryForm({
                    username: "",
                    phone: "",
                    address: "",
                    message: ""
                });
                setInquiryErrors({});
            }
        } catch (error) {
            console.error("Inquiry submission error:", error);
        }
    };

    // Display metadata helpers
    const subCategoryInfo = location.state;

    const heroName = subCategoryInfo?.name || heroProduct?.subCategoryDetails?.[0]?.name;

    const heroDesc = subCategoryInfo?.description || heroProduct?.subCategoryDetails?.[0]?.description;

    const heroImage = (subCategoryInfo?.images?.[0] || heroProduct?.subCategoryDetails?.[0]?.images?.[0])
        ? `${IMAGE_URL}/${(subCategoryInfo?.images?.[0] || heroProduct?.subCategoryDetails?.[0]?.images?.[0]).docPath}/${(subCategoryInfo?.images?.[0] || heroProduct?.subCategoryDetails?.[0]?.images?.[0]).docName}`
        : "/img/product-seal-1.png";

    return (
        <div className="product-specs-page pb-5" style={{ marginTop: "150px", backgroundColor: "#f8f9fa", minHeight: "80vh" }}>
            <div className="container">

                {/* ── Hero Card ─────────────────────────────────────────────────── */}
                {(heroProduct || subCategoryInfo) && (
                    <div className="card border-0 shadow-lg mb-5 overflow-hidden rounded-4">
                        <div className="row g-0">
                            <div className="col-lg-5 bg-white d-flex align-items-center justify-content-center p-5 position-relative">
                                <div className="position-absolute top-0 start-0 w-100 h-100 opacity-10"
                                    style={{ backgroundImage: "radial-gradient(#000 1px, transparent 1px)", backgroundSize: "20px 20px" }}></div>
                                <img
                                    src={heroImage}
                                    alt={heroName}
                                    className="img-fluid position-relative z-1 hover-scale"
                                    style={{ maxHeight: "350px", objectFit: "contain", filter: "drop-shadow(0 15px 30px rgba(0,0,0,0.15))" }}
                                />
                            </div>
                            <div className="col-lg-7" style={{ background: "linear-gradient(135deg, var(--primary-color) 0%, #1a1a2e 100%)" }}>
                                <div className="card-body p-5 d-flex flex-column h-100 justify-content-center text-white">
                                    <h1 className="fw-bold mb-3 display-5 text-white">{heroName}</h1>
                                    <div className="d-flex align-items-center mb-4 flex-wrap gap-2">
                                        <span className="badge px-3 py-2 rounded-pill bg-white text-primary fw-bold shadow-sm">
                                            <i className="bi bi-gear-fill me-2"></i>Industrial Grade
                                        </span>
                                        <span className="badge px-3 py-2 rounded-pill fw-bold shadow-sm" style={{ backgroundColor: "var(--accent-color)", color: "white" }}>
                                            <i className="bi bi-check-circle-fill me-2"></i>In Stock
                                        </span>
                                        {/* Show product type badge */}
                                        {productType === "oring" && (
                                            <span className="badge px-3 py-2 rounded-pill fw-bold shadow-sm bg-info text-dark">
                                                <i className="bi bi-circle me-2"></i>O-Ring Standards
                                            </span>
                                        )}
                                    </div>
                                    <div className="mb-4" style={{ height: "4px", width: "80px", backgroundColor: "var(--accent-color)", borderRadius: "2px" }}></div>
                                    <p className="lead mb-4 text-white-50" style={{ maxWidth: "600px" }}>
                                        {heroDesc}
                                    </p>
                                    <div className="row g-3 mt-auto">
                                        {[
                                            { icon: "bi-shield-check", label: "Quality", value: "ISO 9001:2015" },
                                            { icon: "bi-box-seam", label: "Orders", value: "Bulk & Retail" },
                                            { icon: "bi-truck", label: "Shipping", value: "Fast Delivery" },
                                        ].map(({ icon, label, value }) => (
                                            <div key={label} className="col-md-4">
                                                <div className="d-flex align-items-center p-3 rounded-3 h-100 feature-box"
                                                    style={{ background: "rgba(255,255,255,0.1)", backdropFilter: "blur(5px)", border: "1px solid rgba(255,255,255,0.1)" }}>
                                                    <i className={`bi ${icon} fs-3 text-white me-3 opacity-75`}></i>
                                                    <div>
                                                        <small className="d-block text-white-50 fw-bold text-uppercase" style={{ fontSize: "0.7rem" }}>{label}</small>
                                                        <span className="fw-bold">{value}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                {/* Initial Loading (Full page) */}
                {loading && !heroProduct && !subCategoryInfo && (
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div>
                    </div>
                )}


                {/* ── Search Bar ──────────────────────────────────────────────── */}
                {productType !== "others" && (
                    <div className="card border-0 shadow-sm rounded-4 mb-4">
                        <div className="card-body p-4">
                            <div className="row g-3 align-items-center flex-nowrap pb-2 custom-scrollbar" style={{ overflowX: 'auto', overflowY: 'hidden' }}>
                                <div className="col-md-2">
                                    <div className="input-group">
                                        <span className="input-group-text bg-white border-end-0 ps-3"><i className="bi bi-search text-muted"></i></span>
                                        <input type="text" className="form-control border-start-0"
                                            placeholder="Search..."
                                            value={searchTerm}
                                            onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }} />
                                    </div>
                                </div>

                                {productType === "oring" && (
                                    <div className="col-md-3" style={{ minWidth: "250px" }}>
                                        <Select
                                            options={materialOptionsList.map((m) => ({
                                                value: m._id,
                                                label: m.name
                                            }))}
                                            value={materialOptionsList.find(m => m._id === selectedMaterialId)
                                                ? {
                                                    value: selectedMaterialId,
                                                    label: materialOptionsList.find(m => m._id === selectedMaterialId).name
                                                }
                                                : null}
                                            onChange={(option) => {
                                                setSelectedMaterialId(option ? option.value : "");
                                                setCurrentPage(1);
                                            }}
                                            isClearable
                                            placeholder="All Materials"
                                            classNamePrefix="select"
                                            styles={customStyles}
                                            menuPortalTarget={document.body}
                                        />
                                    </div>
                                )}

                                {/* Dynamic Attribute Filters */}
                                {productType === "oring" && attributeConfigs.map(conf => (
                                    <div className="col-md-2" key={conf._id || conf.name} style={{ minWidth: "150px" }}>
                                        <Select
                                            options={(attributeOptions[conf.name] || []).map(opt => ({ value: opt, label: opt }))}
                                            value={selectedFilters[conf.name] ? { value: selectedFilters[conf.name], label: selectedFilters[conf.name] } : null}
                                            onChange={(option) => {
                                                setSelectedFilters(prev => ({ ...prev, [conf.name]: option ? option.value : "" }));
                                                setCurrentPage(1);
                                            }}
                                            isClearable
                                            placeholder={`All ${conf.name}`}
                                            classNamePrefix="select"
                                            styles={customStyles}
                                            menuPortalTarget={document.body}
                                        />
                                    </div>
                                ))}



                                {/* {(Object.keys(selectedFilters).some(k => selectedFilters[k]) || selectedMaterialId || selectedProductId) && (
                                    <div className="col-md-1">
                                        <button
                                            className="btn btn-link btn-sm p-0 text-decoration-none text-danger"
                                            onClick={() => {
                                                setSelectedFilters({});
                                                setSelectedMaterialId("");
                                                setSelectedProductId("");
                                                setCurrentPage(1);
                                            }}
                                        >
                                            <i className="bi bi-x-circle me-1"></i> Clear
                                        </button>
                                    </div>
                                )} */}
                            </div>
                        </div>
                    </div>
                )}

                {heroProduct ? (
                    <>
                        {productType === "others" ? (
                            <div className="card border-0 shadow-lg rounded-4 overflow-hidden mb-4">
                                <div className="row g-0">
                                    <div className="col-lg-6 p-4 bg-white">
                                        <h2 className="fw-bold mb-2" style={{ color: "var(--primary-color)" }}>Product Inquiry</h2>
                                        <p className="text-muted mb-3">Interested in <strong>{heroProduct.productName}</strong>? Fill out the form below and our team will get back to you with the technical specifications and pricing.</p>
                                        <form onSubmit={handleInquirySubmit}>
                                            <div className="mb-2">
                                                <label className="form-label fw-semibold text-start d-block">Your Name <span className="text-danger">*</span></label>
                                                <input
                                                    type="text"
                                                    className={`form-control p-2 border-2 ${inquiryErrors.username ? 'is-invalid' : ''}`}
                                                    name="username"
                                                    value={inquiryForm.username}
                                                    onChange={handleInquiryChange}
                                                    placeholder="Enter your full name"
                                                />
                                                {inquiryErrors.username && <div className="text-danger small mt-1 text-start">{inquiryErrors.username}</div>}
                                            </div>
                                            <div className="mb-2">
                                                <label className="form-label fw-semibold text-start d-block">Phone Number <span className="text-danger">*</span></label>
                                                <input
                                                    type="tel"
                                                    className={`form-control p-2 border-2 ${inquiryErrors.phone ? 'is-invalid' : ''}`}
                                                    name="phone"
                                                    value={inquiryForm.phone}
                                                    onChange={handleInquiryChange}
                                                    placeholder="Enter your phone number"
                                                />
                                                {inquiryErrors.phone && <div className="text-danger small mt-1 text-start">{inquiryErrors.phone}</div>}
                                            </div>
                                            <div className="mb-2">
                                                <label className="form-label fw-semibold text-start d-block">Address <span className="text-danger">*</span></label>
                                                <textarea
                                                    className={`form-control p-2 border-2 ${inquiryErrors.address ? 'is-invalid' : ''}`}
                                                    name="address"
                                                    rows="2"
                                                    value={inquiryForm.address}
                                                    onChange={handleInquiryChange}
                                                    placeholder="Enter your location/address"
                                                ></textarea>
                                                {inquiryErrors.address && <div className="text-danger small mt-1 text-start">{inquiryErrors.address}</div>}
                                            </div>
                                            <div className="mb-2">
                                                <label className="form-label fw-semibold text-start d-block">Message <span className="text-danger">*</span></label>
                                                <textarea
                                                    className={`form-control p-2 border-2 ${inquiryErrors.message ? 'is-invalid' : ''}`}
                                                    name="message"
                                                    rows="3"
                                                    value={inquiryForm.message}
                                                    onChange={handleInquiryChange}
                                                    placeholder="How can we help you?"
                                                ></textarea>
                                                {inquiryErrors.message && <div className="text-danger small mt-1 text-start">{inquiryErrors.message}</div>}

                                            </div>
                                            <button type="submit" className="btn btn-primary w-100 p-2 fw-bold rounded-3 shadow">
                                                <i className="bi bi-send-fill me-2"></i> Send Inquiry
                                            </button>
                                        </form>
                                    </div>
                                    <div className="col-lg-6 d-none d-lg-block" style={{ background: "url('/img/product-seal-1.png') center/cover no-repeat", minHeight: "400px", position: "relative" }}>
                                        <div className="position-absolute top-0 start-0 w-100 h-100" style={{ background: "linear-gradient(to right, white, transparent)" }}></div>
                                        <div className="d-flex flex-column justify-content-center h-100 p-4 position-relative z-1">
                                            <div className="badge  px-3 py-2 rounded-pill w-fit mb-3" style={{ backgroundColor: "var(--accent-color)" }}>Professional Support</div>
                                            <h3 className="display-6 fw-bold mb-4">Need Custom Solutions?</h3>
                                            <ul className="list-unstyled">
                                                <li className="mb-3 d-flex align-items-center"><i className="bi bi-check2-circle fs-4 text-primary me-3"></i> Expert technical advice</li>
                                                <li className="mb-3 d-flex align-items-center"><i className="bi bi-check2-circle fs-4 text-primary me-3"></i> Bulk pricing options</li>
                                                <li className="mb-3 d-flex align-items-center"><i className="bi bi-check2-circle fs-4 text-primary me-3"></i> Custom dimensions available</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className={`card border-0 shadow-lg rounded-4 overflow-hidden position-relative ${loading ? 'opacity-50' : ''}`} style={{ transition: 'opacity 0.2s' }}>
                                {loading && (
                                    <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center" style={{ zIndex: 10, backgroundColor: 'rgba(255,255,255,0.1)' }}>
                                        <div className="spinner-border text-primary" role="status"></div>
                                    </div>
                                )}
                                <div className="table-responsive">
                                    <table className="table table-hover align-middle mb-0 custom-specs-table">
                                        {productType === "oring" && (
                                            <thead className="small text-uppercase fw-bold" style={{ backgroundColor: "var(--primary-color)" }}>
                                                <tr>
                                                    <th className="py-3 px-3 text-white" style={{ backgroundColor: "var(--primary-color)" }}>S.No</th>
                                                    {[
                                                        "Standard", "Product", "Part No.", "ID (mm)", "±", "CS (mm)", "±",
                                                        ...oringCols.map(c => c.label),
                                                        /* "SKU", */ "Price", "Qty", "Action"
                                                    ].map((h, idx) => (
                                                        <th key={idx} className="py-3 px-3 text-white" style={{ backgroundColor: "var(--primary-color)", whiteSpace: "nowrap" }}>{h}</th>
                                                    ))}
                                                </tr>
                                            </thead>
                                        )}

                                        {productType === "variant" && (
                                            <thead className="small text-uppercase fw-bold" style={{ backgroundColor: "var(--primary-color)" }}>
                                                <tr>
                                                    <th className="py-3 px-3 text-white" style={{ backgroundColor: "var(--primary-color)" }}>S.No</th>
                                                    {variantCols.map((col) => (
                                                        <th key={col.key} className="py-3 px-3 text-white" style={{ backgroundColor: "var(--primary-color)", whiteSpace: "nowrap" }}>
                                                            {col.label}
                                                        </th>
                                                    ))}
                                                    <th className="py-3 px-3 text-white text-center" style={{ backgroundColor: "var(--primary-color)" }}>Price</th>
                                                    <th className="py-3 px-3 text-white text-center" style={{ backgroundColor: "var(--primary-color)", width: "90px" }}>Qty</th>
                                                    <th className="py-3 px-3 text-white text-center" style={{ backgroundColor: "var(--primary-color)" }}>Action</th>
                                                </tr>
                                            </thead>
                                        )}

                                        <tbody>
                                            {currentRows.length > 0 ? (
                                                currentRows.map((item, rowIdx) => {
                                                    const currentPrice = item.pricing?.find(p => p.currencyId === selectedCurrency?.value)?.price;
                                                    const isOutOfStock = Number(item.stock || 0) <= 0;
                                                    const isPriceMissing = !currentPrice || Number(currentPrice) <= 0;
                                                    const isDisabled = isOutOfStock || isPriceMissing;

                                                    return (
                                                        <tr key={item.uniqueId}>
                                                            {productType === "oring" && (
                                                                <>
                                                                    <td className="text-muted small ps-3">{indexOfFirst + rowIdx + 1}</td>
                                                                    <td>
                                                                        <span className={`badge rounded-pill px-2 py-1 ${item.standard === "AS 568A" ? "bg-primary" : "bg-success"}`} style={{ fontSize: "10px" }}>
                                                                            {item.standard}
                                                                        </span>
                                                                    </td>
                                                                    <td className="small fw-semibold">{item.productName}</td>
                                                                    <td className="fw-bold font-monospace" style={{ color: "var(--primary-color)" }}>{item.partNo}</td>
                                                                    <td>{item.id}</td>
                                                                    <td><span className="badge bg-light text-dark border fw-medium px-2 py-1 rounded-pill">{item.idTolerance}</span></td>
                                                                    <td>{item.cs}</td>
                                                                    <td><span className="badge bg-light text-dark border fw-medium px-2 py-1 rounded-pill">{item.csTolerance}</span></td>
                                                                    {oringCols.map((col) => (
                                                                        <td key={col.key} className="small text-muted text-center">
                                                                            {item[col.key] ?? "—"}
                                                                        </td>
                                                                    ))}
                                                                    {/* <td className="text-muted small font-monospace">{item.sku}</td> */}
                                                                    <td className="fw-semibold" style={{ color: "var(--primary-color)" }}>
                                                                        {currentPrice ? (
                                                                            `${item.currencyDetails?.symbol || '₹'}${currentPrice}${item.currencyDetails?.code ? ` ${item.currencyDetails.code}` : ''}`
                                                                        ) : "—"}
                                                                    </td>
                                                                </>
                                                            )}

                                                            {productType === "variant" && (
                                                                <>
                                                                    <td className="text-muted small ps-3">{indexOfFirst + rowIdx + 1}</td>
                                                                    {variantCols.map((col) => (
                                                                        <td key={col.key}>
                                                                            {col.key === "customermrp" || col.key === "price" ? (
                                                                                <span className="fw-semibold" style={{ color: "var(--primary-color)" }}>
                                                                                    {currentPrice ? (
                                                                                        `${item.currencyDetails?.symbol || '₹'}${currentPrice}${item.currencyDetails?.code ? ` ${item.currencyDetails.code}` : ''}`
                                                                                    ) : "—"}
                                                                                </span>
                                                                            )
                                                                                /* : col.key === "sku" ? (
                                                                                     <span className="text-muted small font-monospace">{item[col.key] || "—"}</span>
                                                                                 ) */ : (
                                                                                    <span>{item[col.key] ?? "—"}</span>
                                                                                )}
                                                                        </td>
                                                                    ))}
                                                                    <td className="fw-semibold" style={{ color: "var(--primary-color)" }}>
                                                                        {currentPrice ? (
                                                                            `${item.currencyDetails?.symbol || '₹'}${currentPrice}${item.currencyDetails?.code ? ` ${item.currencyDetails.code}` : ''}`
                                                                        ) : "—"}
                                                                    </td>
                                                                </>
                                                            )}

                                                            <td className="d-flex justify-content-center ">
                                                                <input type="number" className="form-control form-control-sm text-center border-secondary-subtle"
                                                                    min="1" max={item.stock || ""} value={quantities[item.uniqueId] || 1}
                                                                    onChange={(e) => handleQuantityChange(item.uniqueId, e.target.value, item.stock)}
                                                                    style={{ width: "70px" }} />
                                                            </td>


                                                            <td className="pe-4 text-center">
                                                                <button
                                                                    className={`btn ${isOutOfStock ? 'btn-secondary' : 'btn-primary'} btn-sm px-3 py-2 shadow-sm d-flex align-items-center mx-auto`}
                                                                    onClick={() => handleAddToCart(item)}
                                                                    disabled={isDisabled}
                                                                    style={{ minWidth: "140px", whiteSpace: "nowrap", justifyContent: "center" }}
                                                                >
                                                                    {isOutOfStock ? (
                                                                        <><i className="bi bi-x-circle me-2"></i>Out of Stock</>
                                                                    ) : (
                                                                        <><i className="bi bi-cart-plus me-2"></i>Add to Cart</>
                                                                    )}
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    );
                                                })
                                            ) : (
                                                <tr>
                                                    <td colSpan="20" className="text-center py-5 text-muted">
                                                        <i className="bi bi-box-seam display-6 d-block mb-3 opacity-25"></i>
                                                        {searchTerm || Object.values(selectedFilters).some(v => v)
                                                            ? "No matching records found."
                                                            : "No size variants available for this product."}
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>

                                {/* {totalPages > 1 && (
                                    <div className="card-footer bg-white border-top-0 py-3">
                                        <nav>
                                            <ul className="pagination justify-content-center align-items-center mb-0">
                                                <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                                                    <button className="page-link" onClick={() => paginate(currentPage - 1)}>
                                                        <i className="bi bi-chevron-double-left"></i>
                                                    </button>
                                                </li>
                                                {(() => {
                                                    const pages = [];
                                                    const maxVisible = 5;
                                                    let start = 1;

                                                    // Stable logic: Keep 1-5 fixed as long as possible.
                                                    // Only shift when current page exceeds 5.
                                                    if (currentPage > maxVisible) {
                                                        start = currentPage - maxVisible + 1;
                                                    }

                                                    let end = Math.min(totalPages, start + maxVisible - 1);

                                                    // Adjust start if we are near the end to always show maxVisible
                                                    if (end - start + 1 < maxVisible) {
                                                        start = Math.max(1, end - maxVisible + 1);
                                                    }

                                                    for (let i = start; i <= end; i++) {
                                                        pages.push(
                                                            <li key={i} className={`page-item ${currentPage === i ? "active" : ""}`}>
                                                                <button className="page-link" onClick={() => paginate(i)}>{i}</button>
                                                            </li>
                                                        );
                                                    }
                                                    return pages;
                                                })()}
                                                <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                                                    <button className="page-link" onClick={() => paginate(currentPage + 1)}>
                                                        <i className="bi bi-chevron-double-right"></i>
                                                    </button>
                                                </li>
                                            </ul>
                                        </nav>
                                    </div>
                                )} */}

                                {totalPages > 1 && (
                                    <div className="card-footer bg-white border-top-0 py-3">
                                        <div className="d-flex justify-content-center align-items-center gap-3">
                                            <button
                                                onClick={() => paginate(currentPage - 1)}
                                                disabled={currentPage === 1}
                                                style={{
                                                    background: "var(--bs-white)",
                                                    border: "1px solid #dee2e6",
                                                    borderRadius: "8px",
                                                    padding: "8px 18px",
                                                    fontSize: "16px",
                                                    fontWeight: 500,
                                                    color: currentPage === 1 ? "#adb5bd" : "var(--primary-color)",
                                                    cursor: currentPage === 1 ? "not-allowed" : "pointer",
                                                    opacity: currentPage === 1 ? 0.5 : 1,
                                                    transition: "all 0.2s"
                                                }}
                                            >
                                                ‹ Previous
                                            </button>

                                            <span style={{ fontSize: "16px", color: "#6c757d" }}>
                                                Page <b style={{ color: "#212529" }}>{currentPage}</b> of {totalPages}
                                            </span>

                                            <button
                                                onClick={() => paginate(currentPage + 1)}
                                                disabled={currentPage === totalPages}
                                                style={{
                                                    background: currentPage === totalPages ? "#adb5bd" : "var(--primary-color)",
                                                    border: "none",
                                                    borderRadius: "8px",
                                                    padding: "8px 18px",
                                                    fontSize: "16px",
                                                    fontWeight: 500,
                                                    color: "#fff",
                                                    cursor: currentPage === totalPages ? "not-allowed" : "pointer",
                                                    opacity: currentPage === totalPages ? 0.6 : 1,
                                                    transition: "all 0.2s"
                                                }}
                                            >
                                                Next ›
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                ) : !loading && (
                    <div className="card border-0 shadow-sm rounded-4 bg-white mb-5">
                        <div className="card-body p-5 text-center">
                            <i className="bi bi-search display-1 text-muted opacity-25 mb-3 d-block"></i>
                            <h2 className="fw-bold text-dark">No Results Found</h2>
                            {/* <p className="text-muted">Try adjusting your search or filters to find what you're looking for.</p> */}
                        </div>
                    </div>
                )}
            </div>

            <style>{`
        .hover-scale, .hover-scale:hover { transform: none !important; transition: none !important; }
        .feature-box, .feature-box:hover { transform: none !important; transition: none !important; background: rgba(255,255,255,0.1) !important; }
        .card, .card:hover { transform: none !important; transition: transform 0s !important; }
        .custom-specs-table tbody tr { transition: background-color 0.2s ease; }
        .custom-specs-table tbody tr:hover td { background-color: #f8fafc; }
        .page-link { 
            background-color: #edf2f7 !important; 
            color: var(--primary-color) !important; 
            border: none; 
            margin: 0 5px; 
            border-radius: 10px !important; 
            width: 44px; 
            height: 44px; 
            display: flex; 
            align-items: center; 
            justify-content: center; 
            font-weight: 700; 
            transition: all 0.2s ease; 
        }
        .page-item.active .page-link { 
            background-color: var(--primary-color) !important; 
            color: white !important; 
        }
        .page-item.disabled .page-link { 
            opacity: 0.4;
            cursor: not-allowed;
            background-color: #f8f9fa !important;
        }
        .page-link:hover:not(.active) {
            background-color: #e2e8f0 !important;
        }

        /* Custom Scrollbar for Filters */
        .custom-scrollbar::-webkit-scrollbar {
            height: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
            background: #f1f1f1;
            border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
            background: #ccc;
            border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
            background: var(--primary-color);
        }
        .custom-scrollbar > div {
            flex: 0 0 auto;
            width: auto;
            min-width: 150px;
        }
      `}</style>
        </div>
    );
};

export default ProductSpecs;
